/**
 * useConsent — Cookie/RGPD consent hook.
 *
 * Storage: localStorage key "validated_consent".
 * Expires after 6 months (CNIL recommendation).
 *
 * Categories:
 *   - essential   : always true (session, admin token, popup dismissal)
 *   - analytics   : PostHog
 *   - marketing   : reserved for future use
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const STORAGE_KEY = 'validated_consent';
const CONSENT_VERSION = '1.0';
const MAX_AGE_MS = 1000 * 60 * 60 * 24 * 180; // 6 months
const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const API_URL = process.env.REACT_APP_BACKEND_URL;

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.timestamp) return null;
    const age = Date.now() - new Date(parsed.timestamp).getTime();
    if (age > MAX_AGE_MS) return null;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

let posthogInitialized = false;

/** Lazy-load PostHog and initialise ONLY when analytics consent is granted. */
const initAnalytics = () => {
  if (posthogInitialized || typeof window === 'undefined') return;
  if (window.posthog && typeof window.posthog.capture === 'function') {
    posthogInitialized = true;
    return;
  }
  // Minimal PostHog snippet (dynamically injected)
  (function (t, e) {
    let o, n, p, r;
    if (e.__SV) return;
    window.posthog = e;
    e._i = [];
    e.init = function (i, s, a) {
      function g(t, e) {
        const o = e.split('.');
        if (o.length === 2) {
          t = t[o[0]];
          e = o[1];
        }
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      }
      p = t.createElement('script');
      p.type = 'text/javascript';
      p.crossOrigin = 'anonymous';
      p.async = true;
      p.src = s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') + '/static/array.js';
      r = t.getElementsByTagName('script')[0];
      r.parentNode.insertBefore(p, r);
      let u = e;
      if (typeof a !== 'undefined') {
        u = e[a] = [];
      } else {
        a = 'posthog';
      }
      u.people = u.people || [];
      u.toString = function (t) {
        let e = 'posthog';
        if (a !== 'posthog') e += '.' + a;
        if (!t) e += ' (stub)';
        return e;
      };
      u.people.toString = function () { return u.toString(1) + '.people (stub)'; };
      o = 'init capture register register_once unregister identify setPersonProperties group reset opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing'.split(' ');
      for (n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
    };
    e.__SV = 1;
  })(document, window.posthog || []);
  window.posthog.init(POSTHOG_KEY, { api_host: POSTHOG_HOST, person_profiles: 'identified_only' });
  posthogInitialized = true;
};

const stopAnalytics = () => {
  if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
    try { window.posthog.opt_out_capturing(); } catch { /* noop */ }
  }
};

const logConsent = (consent, source) => {
  try {
    axios.post(`${API_URL}/api/rgpd/consent`, {
      essential: true,
      analytics: !!consent.analytics,
      marketing: !!consent.marketing,
      version: CONSENT_VERSION,
      source,
    }).catch(() => {});
  } catch { /* noop */ }
};

export const useConsent = () => {
  const [consent, setConsent] = useState(() => readStored());

  // Apply side-effects when consent changes (init/stop analytics)
  useEffect(() => {
    if (!consent) return;
    if (consent.analytics) {
      initAnalytics();
    } else {
      stopAnalytics();
    }
  }, [consent]);

  const save = useCallback((next, source = 'banner') => {
    const record = {
      version: CONSENT_VERSION,
      essential: true,
      analytics: !!next.analytics,
      marketing: !!next.marketing,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsent(record);
    logConsent(record, source);
  }, []);

  const acceptAll = useCallback(() => save({ analytics: true, marketing: true }, 'banner-accept-all'), [save]);
  const rejectAll = useCallback(() => save({ analytics: false, marketing: false }, 'banner-reject-all'), [save]);
  const savePreferences = useCallback((prefs) => save(prefs, 'preferences'), [save]);
  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setConsent(null);
  }, []);

  return {
    consent,
    hasChosen: !!consent,
    acceptAll,
    rejectAll,
    savePreferences,
    reset,
  };
};

export default useConsent;
